import { useEffect, useMemo } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useDepartmentNurseTypes } from "../../hooks/useDepartmentNurseTypes";
import { usePredictCostBatch } from "../../hooks/usePredictCost";
import { useRevealWage } from "../../hooks/useRevealWage";
import type { CostPredictionResponse, Nurse } from "../../utils/models";
import type { ShiftAvailabilityWindow } from "../../utils/shiftAvailability";

/** The batch always asks for at least one row per page, even while the roster is still empty. */
const MIN_PREDICTION_PAGE_SIZE = 1;

const INVALID_WINDOW_EVENT = "add_staff_cost_prediction_invalid_window";

/** One shared empty map, so "no predictions" doesn't rebuild every row on each render. */
const NO_PREDICTIONS: Record<number, CostPredictionResponse> = {};

/** Why the picked window can't be predicted — `null` when there is no window or both ends are valid. */
const describeInvalidWindow = (window: ShiftAvailabilityWindow | null) => {
	if (!window || (window.start.isValid && window.end.isValid)) return null;
	return {
		start_invalid_reason: window.start.invalidReason,
		start_invalid_explanation: window.start.invalidExplanation,
		end_invalid_reason: window.end.invalidReason,
		end_invalid_explanation: window.end.invalidExplanation,
	};
};

/**
 * A window with an invalid end is an upstream bug (calculateShiftTimes, the custom-hours pick or a request's
 * locked times), and it has no ISO form to predict for. Report each such window as it's picked, so it
 * can't hide as "no prediction".
 */
const useReportInvalidWindow = (window: ShiftAvailabilityWindow | null) => {
	const { trackError } = useAnalytics();
	const invalid = useMemo(() => describeInvalidWindow(window), [window]);
	useEffect(() => {
		if (invalid) trackError(INVALID_WINDOW_EVENT, invalid);
	}, [invalid, trackError]);
};

/**
 * Predicted cost + overtime for every department nurse on the picked window, in ONE request.
 *
 * The candidate set is the whole department roster (department roles only), not the visible list, so the
 * query key moves only on genuine cost inputs — the window, the department and the step-1 job — and never
 * on a view filter (search, contract, the charge limit). Nurses don't affect each other's overtime, so
 * every row's figure stays valid whoever else gets picked. Nothing is fetched until a shift is picked or
 * when the user can't see wages.
 *
 * `isAwaitingWindow` is true until THIS window's figures have arrived: on the first load, and while a newly
 * picked window refetches — the batch keeps showing the previous window's predictions meanwhile
 * (keepPreviousData), which must never feed this window's overtime warning or cost.
 */
export const useAddStaffCostPredictions = (window: ShiftAvailabilityWindow | null, jobId: string | undefined, nurses: Nurse[]) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const canViewWage = useRevealWage();
	useReportInvalidWindow(window);

	const candidateIds = useMemo<number[]>(
		() => nurses.filter((nurse) => departmentNurseTypes.includes(nurse.role)).map((nurse) => nurse.id),
		[nurses, departmentNurseTypes],
	);

	const params = useMemo(() => {
		if (!canViewWage || !window || candidateIds.length === 0) return null;
		const startTime = window.start.toISO();
		const endTime = window.end.toISO();
		// DEFAULT: an invalid DateTime has no ISO form, so that window (reported by useReportInvalidWindow) gets
		// no prediction — every row shows "-" and no OT badge, as with wages hidden, rather than a guessed figure.
		if (!startTime || !endTime) return null;
		return { departmentId: chosenDepartment.department_id, nurseIds: candidateIds, startTime, endTime, ...(jobId ? { jobId } : {}) };
	}, [canViewWage, window, candidateIds, chosenDepartment.department_id, jobId]);

	const { predictions, isLoading, isPlaceholderData } = usePredictCostBatch(params, Math.max(candidateIds.length, MIN_PREDICTION_PAGE_SIZE));
	// Nothing is predicted for this window: never show the previous one's figures (the disabled query still
	// serves them as placeholder data), and there is nothing to wait for.
	if (!params) return { predictions: NO_PREDICTIONS, isLoading: false, isAwaitingWindow: false };
	return { predictions, isLoading, isAwaitingWindow: isLoading || isPlaceholderData };
};
